import React, { useEffect, useCallback, useState, useLayoutEffect, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { toggleFollowedArtistsSidebar, setFollowedArtistsSidebarWidth } from '../redux/slices/followedArtistsSlice';
import { fetchFollowedArtists, unfollowArtist, setArtistNotify } from '../redux/thunk/followedArtistsThunk';
import { fetchFollowedLabels, unfollowLabel } from '../redux/thunk/followedLabelsThunk';
import { openMixpediaLabel } from '../redux/thunk/appNavThunk';
import { setActiveSearch, setArtistQuery } from '../redux/slices/djpalSlice';
import { searchArtist } from '../redux/thunk/djpalThunk';
import { setActiveTab } from '../redux/slices/appSlice';
import { useToast } from '../contexts/ToastContext';
import { useScrollMemory } from '../hooks/useScrollMemory';
import { useIsMobileWeb } from '../hooks/useIsMobileWeb';
import MobileListSearch, { matchesQuery } from './common/MobileListSearch';
import SwipeToRevealRow from './SwipeToRevealRow';
import NotifyBellButton from './NotifyBellButton';
import SidebarSearchField from './common/SidebarSearchField';
import ViewToggle, { useViewMode } from './common/ViewToggle';
import { useDesignV2 } from '../utils/designV2';
import './FollowedArtistsSidebar.scss';
import './TrackContextMenu.scss';
import CoverArt from './CoverArt';

// Mobile-only ⋯ menu for an artist row. On desktop the row reveals the
// X (unfollow) on hover; on touch devices that hover state fires while
// the user is just trying to scroll, marking artists as unfollow
// candidates. Mobile uses this kebab menu instead.
const ArtistRowMenu = ({ artist, onUnfollow, notifyOn, onToggleNotify }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useLayoutEffect(() => {
        if (!open || !btnRef.current) {
            if (!open) setStyle(null);
            return;
        }
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 100; // two items (notify toggle + unfollow)
        const openUp = spaceBelow < dropdownHeight;
        setStyle({
            position: 'fixed',
            right: window.innerWidth - rect.right,
            ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            zIndex: 2000,
        });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="track-more-wrapper artist-row-menu" onClick={(e) => e.stopPropagation()}>
            <button
                ref={btnRef}
                className="track-more-btn artist-more-btn"
                title="More options"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2.5" />
                    <circle cx="12" cy="12" r="2.5" />
                    <circle cx="19" cy="12" r="2.5" />
                </svg>
            </button>
            {open && style && ReactDOM.createPortal(
                <div className="track-context-dropdown" ref={menuRef} style={style} onClick={(e) => e.stopPropagation()}>
                    <div
                        className="dropdown-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                            onToggleNotify(artist);
                        }}
                    >
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={notifyOn ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" fill="none" />
                            </svg>
                        </span>
                        {notifyOn ? 'Notifications off' : 'Notifications on'}
                    </div>
                    <div
                        className="dropdown-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                            onUnfollow(artist);
                        }}
                    >
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </span>
                        Unfollow
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

// Phone: rows swipe left to reveal Unfollow (replaces the ⋯ kebab there).
// Desktop keeps the hover ✕; a narrow hover-capable window keeps the kebab.
const isTouchPhone = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches
    && window.matchMedia('(hover: none)').matches;

const FollowedArtistsSidebar = ({ asPage = false }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { artists, total, loading, sidebarOpen, sidebarWidth, notifySet } = useSelector(state => state.followedArtists);
    // Followed labels live on this page too, under the artists (Erez 11-01: for now, then decide on a own list)
    const labels = useSelector(state => state.followedLabels?.labels || []);
    // Leave-and-return keeps the scroll position (module-level store — this
    // component unmounts on every tab switch).
    const scrollRef = useScrollMemory('followed-artists', [artists.length]);
    const { isAuthenticated } = useSelector(state => state.auth);
    const [searchQuery, setSearchQuery] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const isVisible = asPage || sidebarOpen;
    const v2 = useDesignV2();
    const [viewMode, setViewMode] = useViewMode('artists');

    useEffect(() => {
        if (!isResizing) return;
        const handleMouseMove = (e) => {
            const rail = 52;
            const newWidth = e.clientX - rail;
            if (newWidth >= 240 && newWidth <= 520) {
                dispatch(setFollowedArtistsSidebarWidth(newWidth));
            }
        };
        const handleMouseUp = () => setIsResizing(false);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    useEffect(() => {
        if (isVisible && isAuthenticated) {
            dispatch(fetchFollowedArtists());
            dispatch(fetchFollowedLabels());
        }
    }, [isVisible, isAuthenticated, dispatch]);

    const handleUnfollow = useCallback((e, artist) => {
        e.stopPropagation();
        dispatch(unfollowArtist({ artistName: artist.artist_name, from: 'hover_x' }));
        toast.info(`Unfollowed ${artist.artist_name}`);
    }, [dispatch, toast]);

    const handleUnfollowFromMenu = useCallback((artist) => {
        dispatch(unfollowArtist({ artistName: artist.artist_name, from: 'menu' }));
        toast.info(`Unfollowed ${artist.artist_name}`);
    }, [dispatch, toast]);

    const handleUnfollowFromSwipe = useCallback((artist) => {
        dispatch(unfollowArtist({ artistName: artist.artist_name, from: 'swipe' }));
        toast.info(`Unfollowed ${artist.artist_name}`);
    }, [dispatch, toast]);

    const handleToggleNotifyFromMenu = useCallback((artist) => {
        const on = !!notifySet[artist.artist_name];
        dispatch(setArtistNotify({ artistName: artist.artist_name, notify: !on, from: 'sidebar' }));
    }, [dispatch, notifySet]);

    // Sub-tab + query BEFORE setActiveTab so the nav marker is typed and the
    // result replaces it (one Back = back here). setActiveTab sets the category
    // itself — a separate setActiveCategory pushes a stray landing-page entry.
    const handleArtistClick = useCallback((artist) => {
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(artist.artist_name));
        // Same tick as the tab switch: the 150ms delay this had painted the
        // Mixpedia tab's previous page (home or the last artist) first, a flash.
        // artist_public_id rides along from user_favourite_artists — id
        // navigation is exact; the name is only the fallback seed.
        dispatch(setActiveTab('djpal'));
        dispatch(searchArtist({ artistName: artist.artist_name, artistId: artist.artist_public_id || null, artworkUrl: artist.artwork || null }));
    }, [dispatch]);

    const handleLabelClick = (label) => {
        dispatch(openMixpediaLabel({ publicId: label.label_public_id, name: label.label_name, from: 'favourites' }));
    };
    const handleLabelUnfollow = (e, label) => {
        e.stopPropagation();
        dispatch(unfollowLabel({ labelPublicId: label.label_public_id, name: label.label_name, from: 'sidebar' }));
    };
    const q = searchQuery.trim().toLowerCase();
    const shownLabels = labels.filter(l => !q || (l.label_name || '').toLowerCase().includes(q));

    if (!isVisible) return null;

    const handleClose = () => {
        if (asPage) {
            // The in-page arrow is "up to Library", not browser Back: this
            // page is a Library sub-page, and history.back() sent the user
            // to whatever tab they visited before Library (e.g. Digger).
            // The OS/browser back gesture still walks history as before.
            dispatch(setActiveTab('library'));
        } else {
            dispatch(toggleFollowedArtistsSidebar());
        }
    };

    return (
        <div className={`followed-artists-sidebar${asPage ? ' as-page' : ''}`} style={asPage ? undefined : { width: `${sidebarWidth}px` }}>
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Favourite Artists</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={handleClose}
                        title={asPage ? 'Back' : 'Hide Favourite Artists'}
                    >
                        {asPage ? '←' : '‹'}
                    </button>
                </div>
                <span className="artist-count">{total} artist{total !== 1 ? 's' : ''}{labels.length > 0 ? ` · ${labels.length} label${labels.length !== 1 ? 's' : ''}` : ''}</span>
                <div className="search-input-wrapper">
                    {isMobileWeb ? (
                        <MobileListSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search artists"
                            ariaLabel="Search favourite artists"
                            overlay={{
                                fetch: (q) => artists
                                    .filter(a => matchesQuery(a.artist_name, q))
                                    .map((a, i) => ({ key: `${a.artist_name}-${i}`, title: a.artist_name, artworkUrl: a.artwork || null, round: true, item: a })),
                                onSelect: (row) => handleArtistClick(row.item),
                            }}
                        />
                    ) : (
                        <SidebarSearchField
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search artists"
                            ariaLabel="Search favourite artists"
                        />
                    )}
                </div>
                {v2 && !asPage && <ViewToggle mode={viewMode} onChange={setViewMode} />}
            </div>

            <div className="sidebar-content" ref={scrollRef}>
                {loading && artists.length === 0 ? (
                    <div className="text-center p-4"><Spinner animation="border" size="sm" /></div>
                ) : artists.length === 0 && labels.length === 0 ? (
                    <div className="empty-state">
                        <svg className="empty-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                        <p>No favourite artists yet</p>
                        <p className="empty-hint">Star an artist in Mixpedia to save them here</p>
                    </div>
                ) : (
                    <div className={`artists-list${v2 && !asPage && viewMode === 'list' ? ' is-list' : ''}`}>
                        {artists.filter(a => !searchQuery.trim() || a.artist_name.toLowerCase().includes(searchQuery.toLowerCase())).map((artist, idx) => (
                            <SwipeToRevealRow
                                key={`${artist.artist_name}-${idx}`}
                                enabled={isTouchPhone}
                                actions={
                                    <button type="button" className="swipe-row-action" onClick={() => handleUnfollowFromSwipe(artist)}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                        Unfollow
                                    </button>
                                }
                            >
                            <div
                                className="artist-item"
                                onClick={() => handleArtistClick(artist)}
                            >
                                <div className="artist-avatar">
                                    {artist.artwork ? (
                                        <CoverArt src={artist.artwork} size={40} />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            {(artist.artist_name || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <span className="artist-name">{artist.artist_name}</span>
                                <NotifyBellButton entityType="artist" entity={artist} from="sidebar" />
                                <button
                                    className="unfollow-btn desktop-only-action"
                                    onClick={(e) => handleUnfollow(e, artist)}
                                    title="Unfollow"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                                {/* Phones: swipe-left is the unfollow path — no ⋯ kebab. */}
                                {!isTouchPhone && (
                                    <ArtistRowMenu
                                        artist={artist}
                                        onUnfollow={handleUnfollowFromMenu}
                                        notifyOn={!!notifySet[artist.artist_name]}
                                        onToggleNotify={handleToggleNotifyFromMenu}
                                    />
                                )}
                            </div>
                            </SwipeToRevealRow>
                        ))}
                    </div>
                )}
                {shownLabels.length > 0 && (
                    <div className="followed-labels">
                        <h5 className="followed-labels-title">Labels</h5>
                        <div className={`artists-list${v2 && !asPage && viewMode === 'list' ? ' is-list' : ''}`}>
                            {shownLabels.map((label) => (
                                <div key={label.label_public_id} className="artist-item label-item" onClick={() => handleLabelClick(label)}>
                                    <div className="artist-avatar label-avatar">
                                        {label.artwork ? (
                                            <CoverArt src={label.artwork} size={40} />
                                        ) : (
                                            <div className="avatar-placeholder">{(label.label_name || '?').charAt(0).toUpperCase()}</div>
                                        )}
                                    </div>
                                    <span className="artist-name">{label.label_name}</span>
                                    <NotifyBellButton entityType="label" entity={{ publicId: label.label_public_id, name: label.label_name, artworkUrl: label.artwork }} from="sidebar" />
                                    <button className="unfollow-btn desktop-only-action" onClick={(e) => handleLabelUnfollow(e, label)} title="Unfollow">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"/>
                                            <line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div
                className="sidebar-resizer"
                onMouseDown={() => setIsResizing(true)}
            />
        </div>
    );
};

export default FollowedArtistsSidebar;
